export type AssistantTranscriptEntry = {
  id: string;
  text: string;
  timestamp: string;
  source: 'history' | 'realtime';
  isLive?: boolean;
  responseId?: string;
  replacesResponseId?: string;
  responsePurpose?: string;
  questionId?: number | null;
  assistantOnly?: boolean;
};

type AssistantTranscriptTurn = {
  id: number;
  question_id?: number | null;
  user_transcript: string;
  assistant_transcript: string;
  created_at: string;
  metadata?: Record<string, unknown>;
};

const stringValue = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim() ? value : undefined
);
const displayText = (text: string) => text.replace(/\s+/g, ' ').replace(/\s+([,.!?;:])/g, '$1').trim();
const comparisonText = (text: string) => text.replace(/\s+/g, '');
const isClosing = (entry: AssistantTranscriptEntry) => (
  entry.assistantOnly === true && entry.questionId === null && entry.responsePurpose === 'completion_closing'
);

const projectAssistantTurns = (turns: AssistantTranscriptTurn[]) => {
  const entries: AssistantTranscriptEntry[] = [];
  const suppressedTurnIds = new Set<string>();
  let previousAssistantTurn: AssistantTranscriptTurn | undefined;
  let legacyFragments: AssistantTranscriptEntry[] = [];

  turns.forEach((turn) => {
    const text = displayText(turn.assistant_transcript);
    if (!text) {
      legacyFragments = [];
      return;
    }
    const responseId = stringValue(turn.metadata?.assistant_response_id);
    const assistantOnly = turn.metadata?.assistant_only === true && !turn.user_transcript.trim();
    // Older captures persisted a snapshot of the preceding guide response on
    // the learner row. Restrict the spacing fallback to that exact shape;
    // equal words from distinct provider generations are independent speech.
    const previous = previousAssistantTurn;
    const legacyCaptureEcho = !responseId && Boolean(turn.user_transcript.trim())
      && Boolean(stringValue(turn.metadata?.input_audio_item_id))
      && previous?.metadata?.assistant_only === true && !previous.user_transcript.trim()
      && Boolean(stringValue(previous.metadata.assistant_response_id))
      && previous.question_id !== undefined && previous.question_id === turn.question_id
      && comparisonText(previous.assistant_transcript) === comparisonText(turn.assistant_transcript);
    if (legacyCaptureEcho) {
      suppressedTurnIds.add(`turn-${turn.id}`);
      legacyFragments = [];
      return;
    }
    previousAssistantTurn = turn;
    const entry: AssistantTranscriptEntry = {
      id: `turn-${turn.id}`,
      text,
      timestamp: turn.created_at,
      source: 'history',
      responseId,
      replacesResponseId: stringValue(turn.metadata?.replaces_assistant_response_id),
      responsePurpose: stringValue(turn.metadata?.response_purpose),
      questionId: turn.question_id,
      assistantOnly,
    };
    // A legacy runtime saved separate chunks followed by their cumulative
    // transcript without provider IDs. Recognize only that complete assembly,
    // never one prefix or two identical utterances that might be repeats.
    if (assistantOnly && !responseId && turn.question_id !== undefined) {
      if (legacyFragments.some((fragment) => (
        fragment.questionId !== entry.questionId || fragment.responsePurpose !== entry.responsePurpose
      ))) legacyFragments = [];
      const completeLegacyAssembly = legacyFragments.length >= 2
        && new Set(legacyFragments.map((fragment) => fragment.text)).size === legacyFragments.length
        && legacyFragments.every((fragment) => fragment.text.split(/\s+/).length >= 2
          && comparisonText(fragment.text).length >= 8)
        && legacyFragments.map((fragment) => fragment.text).join(' ') === entry.text;
      if (completeLegacyAssembly) {
        const fragmentIds = new Set(legacyFragments.map((fragment) => fragment.id));
        for (let index = entries.length - 1; index >= 0; index -= 1) {
          if (fragmentIds.has(entries[index].id)) entries.splice(index, 1);
        }
        fragmentIds.forEach((id) => suppressedTurnIds.add(id));
        entry.timestamp = legacyFragments[0].timestamp;
        legacyFragments = [entry];
      } else legacyFragments.push(entry);
    } else legacyFragments = [];
    const index = responseId ? entries.findIndex((candidate) => candidate.responseId === responseId) : -1;
    if (index < 0) entries.push(entry);
    else {
      // The assistant-only row is the actual spoken caption; a learner-row
      // snapshot of the same response cannot replace it with a capture echo.
      const existing = entries[index];
      if (!existing.assistantOnly || assistantOnly) entries[index] = { ...entry, timestamp: existing.timestamp };
      suppressedTurnIds.add(entries[index].id === entry.id ? existing.id : entry.id);
    }
  });
  return { entries, suppressedTurnIds };
};

const reconcileEntries = (entries: AssistantTranscriptEntry[]): AssistantTranscriptEntry[] => {
  const reconciled: AssistantTranscriptEntry[] = [];
  entries.forEach((entry) => {
    if (!entry.text.trim()) return;
    const existingIndex = reconciled.findIndex((candidate) => (
      candidate.id === entry.id || Boolean(entry.responseId && candidate.responseId === entry.responseId)
    ));
    if (existingIndex < 0) reconciled.push(entry);
    else reconciled[existingIndex] = {
      ...reconciled[existingIndex], ...entry,
      timestamp: reconciled[existingIndex].timestamp,
    };
  });
  const replacedResponseIds = new Set<string>();
  reconciled.forEach((entry, index) => {
    if (!isClosing(entry) || !entry.responseId || !entry.replacesResponseId
      || entry.responseId === entry.replacesResponseId) return;
    if (reconciled.slice(0, index).some((candidate) => candidate.responseId === entry.replacesResponseId && isClosing(candidate))) {
      replacedResponseIds.add(entry.replacesResponseId);
    }
  });
  return reconciled.filter((entry) => !entry.responseId || !replacedResponseIds.has(entry.responseId));
};

/** Captions are presentation only: this never changes delivery/progress state. */
export const buildAssistantTranscriptEntries = (turns: AssistantTranscriptTurn[]): AssistantTranscriptEntry[] => (
  reconcileEntries(projectAssistantTurns(turns).entries)
);

/** Reconcile saved/local/live copies by generation, without merging repeated speech. */
export const reconcileAssistantTranscriptEntries = (
  turns: AssistantTranscriptTurn[],
  history: AssistantTranscriptEntry[],
  live: AssistantTranscriptEntry | null,
): AssistantTranscriptEntry[] => {
  const { entries: canonical, suppressedTurnIds } = projectAssistantTurns(turns);
  const byTurn = new Map(canonical.map((entry) => [entry.id, entry]));
  const byResponse = new Map(canonical.filter((entry) => entry.responseId).map((entry) => [entry.responseId, entry]));
  const presented = [...history, ...(live ? [live] : [])].filter((entry) => !suppressedTurnIds.has(entry.id))
    .map((entry) => {
      const saved = byTurn.get(entry.id) ?? (entry.responseId ? byResponse.get(entry.responseId) : undefined);
      return saved ? { ...saved, ...entry } : entry;
    });
  return reconcileEntries(presented);
};
